/**
 * Logger Interface
 *
 * Defines the contract for logger implementations.
 * All loggers must implement these methods to be compatible
 * with the LoggingService.
 *
 * @module services/shared
 */

/**
 * Log level constants
 */
export const LogLevel = {
  DEBUG: 'debug',
  LOG: 'log',
  WARN: 'warn',
  ERROR: 'error'
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

/**
 * Logger interface that all logger implementations must follow
 */
export interface ILogger {
  /**
   * Log a debug message
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  debug(message: string, ...args: unknown[]): void;

  /**
   * Log an informational message
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  log(message: string, ...args: unknown[]): void;

  /**
   * Log a warning message
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  warn(message: string, ...args: unknown[]): void;

  /**
   * Log an error message
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  error(message: string, ...args: unknown[]): void;
}

/**
 * Configuration options for logger implementations
 */
export interface LoggerConfig {
  /**
   * Minimum log level to output
   */
  level?: LogLevel;

  /**
   * Whether to include timestamps in log output
   */
  includeTimestamp?: boolean;

  /**
   * Custom prefix for log messages
   */
  prefix?: string;

  /**
   * Whether the logger is enabled
   */
  enabled?: boolean;
}

/**
 * Log entry structure for advanced logging implementations
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  args: unknown[];
  timestamp: Date;
  prefix?: string;
}